import React from "react";
import clsx from "clsx";

import { ChatBox } from "@chef/components";
import { getPreferenceCombo, PreferenceCombos } from "@chef/helpers";
import {
  getDishCount,
  TASTE_PREFERENCE_SELETION_LIMITS,
} from "@chef/constants";

import { intl } from "./TastePreferencesNotification.Intl";

interface TastePreferencesNotificationProps {
  tastePreferenceIds: string[];
  conceptPreferenceIds: string[];
}

type TastePreferencesLimit = "soft" | "strong" | "max" | undefined;

interface TastePreferencesWarningProps {
  tastePreferencesCount: number;
  warning?: {
    title: string;
    text: string;
  };
}

const textMap = new Map<PreferenceCombos, string | null>([
  [PreferenceCombos.NONE, null],
  [PreferenceCombos.VEGAN, intl.YOU_HAVE_CREATED_A_VEGAN_MEALBOX],
  [PreferenceCombos.VEGETARIAN, intl.YOU_HAVE_CREATED_A_VEGETARIAN_MEALBOX],
  [PreferenceCombos.PESCETARIAN, intl.YOU_HAVE_CREATED_A_PESCETARIAN_MEALBOX],
  [PreferenceCombos.FLEXITARIAN, intl.YOU_HAVE_CREATED_A_FLEXITARIAN_MEALBOX],
]);

const { MAX_LIMIT, STRONG_LIMIT, SOFT_LIMIT } =
  TASTE_PREFERENCE_SELETION_LIMITS;

const TastePreferencesWarning = ({
  tastePreferencesCount,
  warning,
}: TastePreferencesWarningProps) => {
  const reachedMaxLimit = tastePreferencesCount >= MAX_LIMIT;
  const reachedStrongLimit = tastePreferencesCount >= STRONG_LIMIT;

  if (!warning) {
    return null;
  }

  const { title, text } = warning;

  return (
    <ChatBox
      background={
        (reachedMaxLimit && "error") ||
        (reachedStrongLimit && "highlight") ||
        "information"
      }
      className="flex-col justify-center text-sm"
    >
      <strong className={clsx(reachedStrongLimit && "text-error")}>
        {title}
      </strong>
      <p>{text}</p>
    </ChatBox>
  );
};

export const TastePreferencesNotification = ({
  tastePreferenceIds,
  conceptPreferenceIds,
}: TastePreferencesNotificationProps) => {
  const combo = getPreferenceCombo([
    ...conceptPreferenceIds,
    ...tastePreferenceIds,
  ]);

  const text = textMap.get(combo);

  const tastePreferencesCount = tastePreferenceIds.length;

  const maxLimitReached = tastePreferencesCount >= MAX_LIMIT;
  const strongLimitReached = tastePreferencesCount >= STRONG_LIMIT;
  const softLimitReached = tastePreferencesCount >= SOFT_LIMIT;

  let currentLimit: TastePreferencesLimit;

  if (maxLimitReached) {
    currentLimit = "max";
  } else if (strongLimitReached) {
    currentLimit = "strong";
  } else if (softLimitReached) {
    currentLimit = "soft";
  }

  const warning = warningTextMap.get(currentLimit);

  if (text) {
    return (
      <>
        <ChatBox background="information" className="justify-center text-sm">
          <strong>{text}</strong>
        </ChatBox>
        {maxLimitReached && (
          <TastePreferencesWarning
            tastePreferencesCount={tastePreferencesCount}
            warning={warning}
          />
        )}
      </>
    );
  }

  return (
    <TastePreferencesWarning
      tastePreferencesCount={tastePreferencesCount}
      warning={warning}
    />
  );
};

const warningTextMap = new Map<
  TastePreferencesLimit,
  {
    title: string;
    text: string;
  }
>([
  [
    "soft",
    {
      title: intl.HEADS_UP,
      text: intl.THE_MORE_INGREDIENTS_YOU_REMOVE_THE_FEWER_DISHES_WE_CAN_SUGGEST,
    },
  ],
  [
    "strong",
    {
      title: intl.YOU_KNOW_WHAT_YOU_WANT,
      text: intl.WE_WILL_DO_OUR_BEST_BUT_STRONGLY_RECOMMEND_THAT_YOU_CHOOSE_YOUR_OWN_DISHES,
    },
  ],
  [
    "max",
    {
      title: intl.WE_WILL_NOT_BE_ABLE_TO_MATCH_YOUR_PREFERENCES,
      text: intl.WE_WILL_DO_OUR_BEST_TO_MATCH_YOUR_NEEDS(
        getDishCount({ portions: 2 }),
      ),
    },
  ],
]);
